/**
 * OP-1: Dashboard
 * KPIs, live map, alerts feed
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { opsApi } from '@/services/api';
import { useTrackOticAuth } from '@/context/TrackOticAuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { 
  Package, Thermometer, Wifi, AlertTriangle, Bell, 
  TrendingUp, Clock, ChevronRight, RefreshCw
} from 'lucide-react';

// Fix Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const createIcon = (color) => new L.Icon({
  iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${color}.png`,
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

export default function OpsDashboard() {
  const { tenant } = useTrackOticAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [workOrders, setWorkOrders] = useState([]);
  const [deviceHealth, setDeviceHealth] = useState({ summary: {}, devices: [] });
  const [alerts, setAlerts] = useState([]);
  const [liveDevices, setLiveDevices] = useState([]);

  useEffect(() => {
    fetchDashboardData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [woRes, healthRes, alertsRes] = await Promise.all([
        opsApi.getWorkOrders({ limit: 10 }),
        opsApi.getDeviceHealth({}),
        opsApi.getAlerts({ status: 'open', limit: 5 })
      ]);
      setWorkOrders(woRes.data);
      setDeviceHealth(healthRes.data);
      setAlerts(alertsRes.data);
      
      // Extract live device positions
      const devicesWithPos = healthRes.data.devices.filter(d => d.is_online);
      setLiveDevices(devicesWithPos);
    } catch (error) {
      console.error('Dashboard fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const activeWorkOrders = workOrders.filter(wo => ['assigned', 'started', 'in_transit'].includes(wo.status));
  const breachesToday = alerts.filter(a => a.type === 'temp_breach').length;
  const offlineDevices = deviceHealth.summary?.offline || 0;
  const expiringDevices = 0; // Would need entitlement data

  const kpis = [
    { 
      label: 'Active Work Orders', 
      value: activeWorkOrders.length, 
      icon: Package, 
      color: 'blue',
      onClick: () => navigate('/ops/work-orders?status=active')
    },
    { 
      label: 'Temp Breaches Today', 
      value: breachesToday, 
      icon: Thermometer, 
      color: breachesToday > 0 ? 'red' : 'green',
      onClick: () => navigate('/ops/alerts?type=temp_breach')
    },
    { 
      label: 'Offline Devices', 
      value: offlineDevices, 
      icon: Wifi, 
      color: offlineDevices > 0 ? 'amber' : 'green',
      onClick: () => navigate('/ops/devices')
    },
    { 
      label: 'Expiring (7 days)', 
      value: expiringDevices, 
      icon: AlertTriangle, 
      color: expiringDevices > 0 ? 'amber' : 'green',
      onClick: () => navigate('/ops/devices')
    },
  ];

  const colorClasses = {
    blue: 'bg-blue-500/20 text-blue-400',
    green: 'bg-green-500/20 text-green-400',
    amber: 'bg-amber-500/20 text-amber-400',
    red: 'bg-red-500/20 text-red-400',
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-slate-400">Welcome back to {tenant?.name || 'TrackOtic'}</p>
        </div>
        <Button 
          variant="outline" 
          onClick={fetchDashboardData}
          className="border-slate-700 text-slate-300"
          data-testid="refresh-dashboard"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {kpis.map((kpi, i) => {
          const Icon = kpi.icon;
          return (
            <button
              key={i}
              onClick={kpi.onClick}
              className="bg-slate-900 rounded-xl p-4 border border-slate-800 hover:border-slate-700 transition-colors text-left"
              data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="flex items-center justify-between">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses[kpi.color]}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <ChevronRight className="w-4 h-4 text-slate-600" />
              </div>
              <p className="text-3xl font-bold text-white mt-3">{kpi.value}</p>
              <p className="text-slate-400 text-sm">{kpi.label}</p>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Live Map */}
        <div className="lg:col-span-2 bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
          <div className="p-4 border-b border-slate-800 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Live Tracking</h2>
            <Badge variant="outline" className="border-green-500 text-green-400">
              {liveDevices.length} online
            </Badge>
          </div>
          <div className="h-80">
            <MapContainer 
              center={[20.5937, 78.9629]} 
              zoom={5} 
              style={{ height: '100%', width: '100%' }}
              className="z-0"
            >
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                attribution='&copy; OpenStreetMap'
              />
              {liveDevices.map((device, i) => {
                if (!device.current_lat || !device.current_lng) return null;
                return (
                  <Marker 
                    key={i} 
                    position={[parseFloat(device.current_lat), parseFloat(device.current_lng)]}
                    icon={createIcon('green')}
                  >
                    <Popup>
                      <div className="text-sm">
                        <p className="font-bold">{device.device_uid}</p>
                        <p>Temp: {device.current_temp || 'N/A'}°C</p>
                        <p>Battery: {device.battery_level}%</p>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          </div>
        </div>

        {/* Alerts Feed */}
        <div className="bg-slate-900 rounded-xl border border-slate-800">
          <div className="p-4 border-b border-slate-800 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Bell className="w-5 h-5 text-red-400" />
              Open Alerts
            </h2>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/ops/alerts')}
              className="text-blue-400"
            >
              View All
            </Button>
          </div>
          <div className="p-2 max-h-80 overflow-y-auto">
            {alerts.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No open alerts</p>
              </div>
            ) : (
              <div className="space-y-2">
                {alerts.map((alert) => (
                  <div 
                    key={alert.id}
                    className="p-3 bg-slate-800/50 rounded-lg border border-slate-700"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        alert.level === 'critical' ? 'bg-red-500/20' : 'bg-amber-500/20'
                      }`}>
                        <AlertTriangle className={`w-4 h-4 ${
                          alert.level === 'critical' ? 'text-red-400' : 'text-amber-400'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">{alert.message}</p>
                        <p className="text-slate-500 text-xs mt-1">
                          {new Date(alert.triggered_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Work Orders */}
      <div className="mt-6 bg-slate-900 rounded-xl border border-slate-800">
        <div className="p-4 border-b border-slate-800 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Recent Work Orders</h2>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/ops/work-orders')}
            className="text-blue-400"
          >
            View All
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-800">
                <th className="text-left p-4 text-slate-400 font-medium">WO #</th>
                <th className="text-left p-4 text-slate-400 font-medium">Pickup</th>
                <th className="text-left p-4 text-slate-400 font-medium">Drop</th>
                <th className="text-left p-4 text-slate-400 font-medium">Status</th>
                <th className="text-left p-4 text-slate-400 font-medium">Temp Range</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="p-8 text-center">
                    <RefreshCw className="w-6 h-6 animate-spin text-teal-500 mx-auto" />
                  </td>
                </tr>
              ) : workOrders.length === 0 ? (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-slate-500">
                    No work orders found
                  </td>
                </tr>
              ) : (
                workOrders.slice(0, 5).map((wo) => (
                <tr 
                  key={wo.id} 
                  className="border-b border-slate-800 hover:bg-slate-800/50 cursor-pointer"
                  onClick={() => navigate(`/ops/work-orders/${wo.id}`)}
                >
                  <td className="p-4 text-white font-mono text-sm">{wo.work_order_number}</td>
                  <td className="p-4 text-slate-300 text-sm truncate max-w-40">{wo.pickup_address || '-'}</td>
                  <td className="p-4 text-slate-300 text-sm truncate max-w-40">{wo.drop_address || '-'}</td>
                  <td className="p-4">
                    <Badge variant={wo.status === 'completed' ? 'default' : 'secondary'}>
                      {wo.status}
                    </Badge>
                  </td>
                  <td className="p-4 text-slate-300 text-sm">
                    {wo.temp_min && wo.temp_max ? `${wo.temp_min}°C - ${wo.temp_max}°C` : '-'}
                  </td>
                </tr>
              )))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
